import { action, extendObservable } from 'mobx';
import PropTypes from 'prop-types';
import moment from 'moment';
import copyText from '../../../copyText';
import DatepickerStore from '../../DatepickerStore';
import FlagStore from '../../FlagStore';
import FuseSelectStore from '../../FuseSelectStore';
import InputStore from '../../InputStore';
import { PolicyModalSectionProps } from '../PolicyModalStore';

const DEFAULT_VALUES = {
    activations: 0,
    activationsAllowed: 10,
    deactivated: false,
    neverExpire: true,
    preventNewActivations: false,
    unlimitedActivations: true,
};

export function getGmtDate(date) {
    date = date ? moment.utc(date) : moment.utc();
    return date.hours(0).minutes(0).seconds(0).milliseconds(0);
}

export function getGmtDateString(date) {
    return getGmtDate(date).format('MMM/DD/YYYY').toUpperCase();
}

export function getStatus(accessCode) {
    if (!accessCode.neverExpire && getGmtDate(accessCode.expirationDate).isBefore(getGmtDate(), 'day')) {
        return { name: 'Expired', isRed: true };
    }

    if (!accessCode.unlimitedActivations && (Number(accessCode.activations) >= Number(accessCode.activationsAllowed))) {
        return { name: 'Activation Limit Reached', isRed: true };
    }

    return { name: 'Active', isRed: false };
}

export default class AccessCodeInfoStore {
    static MAX_ACTIVATION_LIMIT = 10000;

    static PropType = PropTypes.shape({
        // typing this seems like too much effort and a bunch of copy pasting. Could do it easily if this was TS.
        originalValue: PropTypes.any,

        activations: InputStore.PropType,
        activationsAllowed: InputStore.PropType,
        browsingRole: FuseSelectStore.PropType,
        code: InputStore.PropType,
        deactivated: FlagStore.PropType,
        expirationDate: DatepickerStore.PropType,
        id: PropTypes.number,
        neverExpire: FlagStore.PropType,
        preventNewActivations: FlagStore.PropType,
        limitedActivations: FlagStore.PropType,

        ...PolicyModalSectionProps,
    });

    // ---- THIS IS AN UGLY COPY PASTE. PUT IN A HELPER

    toSelectData = (arr, configure = () => {}) =>
        // Except this. item.name is actually item.code, and status is removed
        arr.map((item) => ({ label: item.name, value: item.id, ...configure(item) }))

    toSelectValue = (arr) => arr.map(({ id }) => id)

    syncSelect = (selectStore, configure) => (arr) =>
        selectStore.setOptions(this.toSelectData(arr, configure));

    setSelectInitialState = (select, items) => {
        select.setOptions(this.toSelectData(items));
        select.setSelectedValues(this.toSelectValue(items));
    }

    // ---
    constructor(data = {}) {
        extendObservable(this, /** @class AccessCodeInfoStore */{
            activations: new InputStore(),
            activationsAllowed: new InputStore(),
            browsingRole: new FuseSelectStore(),
            code: new InputStore(),
            deactivated: new FlagStore(),
            expirationDate: new DatepickerStore({ dateFormat: 'MMM/DD/YYYY' }),
            neverExpire: new FlagStore(),
            preventNewActivations: new FlagStore(),
            limitedActivations: new FlagStore(),
        }, data);

        this.activationsAllowed.setValidator((value) => {
            if (!this.limitedActivations.isTrue) {
                return true;
            }
            const intValue = Number(value);
            const errorMsg = `Enter a value of 1-${AccessCodeInfoStore.MAX_ACTIVATION_LIMIT}`;
            if (this.activationsAllowed.isEmpty
                || !/^\d+$/i.test(value)
                || intValue < 1
                || intValue > AccessCodeInfoStore.MAX_ACTIVATION_LIMIT) {
                return errorMsg;
            }

            if (intValue <= Number(this.activations.value)) {
                return 'Activation limit cannot be less than current activations.';
            }
            return true;
        });
    }

    copyTextToClipboard = () => copyText(this.code.getValue());

    setValue = action((value) => {
        value = { ...DEFAULT_VALUES, ...value };

        // Anything marked with * we want just for display / sending the entire entity when editing, not for mutation
        this.activations.setValue(value.activations); //*
        this.activationsAllowed.setValue(value.activationsAllowed);
        this.setSelectInitialState(this.browsingRole, value.browsingRoleId ?
            [{ id: value.browsingRoleId, name: value.browsingRoleName }] : []); //* except create
        this.code.setValue(value.code); // *
        this.deactivated.setValue(value.deactivated); // *
        this.expirationDate.setStringDate(value.expirationDate);
        this.neverExpire.setValue(!value.neverExpire);
        this.preventNewActivations.setValue(value.preventNewActivations);
        this.limitedActivations.setValue(!value.unlimitedActivations);

        this.originalValue = { ...value };
    });

    zeroOrNumber = (v) => {
        const n = parseInt(v, 10);
        return isNaN(n) ? 0 : n;
    };

    getValue = () => ({
        activations: this.zeroOrNumber(this.activations.getValue()),
        activationsAllowed: this.zeroOrNumber(this.activationsAllowed.getValue()),
        ...this.browsingRole.selectedValues.length ? {
            browsingRoleId: this.browsingRole.selectedValues[0],
            browsingRoleName: this.browsingRole.getLabelForSelectedValue(),
        } : {},
        code: this.code.getValue(),
        deactivated: this.deactivated.getValue(),
        expirationDate: this.expirationDate.date.toISOString(),
        neverExpire: !this.neverExpire.getValue(),
        preventNewActivations: this.preventNewActivations.getValue(),
        unlimitedActivations: !this.limitedActivations.getValue(),
    });

    hasChanged = () => {
        const curV = this.getValue();
        const origV = this.originalValue;

        return origV.activationsAllowed !== curV.activationsAllowed ||
            origV.browsingRoleId !== curV.browsingRoleId ||
            !getGmtDate(origV.expirationDate).isSame(getGmtDate(curV.expirationDate), 'day') ||
            origV.neverExpire !== curV.neverExpire ||
            origV.preventNewActivations !== curV.preventNewActivations ||
            origV.unlimitedActivations !== curV.unlimitedActivations;
    }

    isValid = () => {
        const curV = this.getValue();

        return typeof curV.browsingRoleId !== 'undefined' && this.activationsAllowed.isValid;
    }
}
